import { useQueryClient } from '@tanstack/react-query';
import { App } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { api, ApiError, NetworkError, refreshSession } from '@/api/client';
import { deviceId, deviceLabel } from '@/api/device';
import { tokenStore } from '@/api/tokens';
import { unsubscribeFromPush } from '@/features/doctor/offline/push';
import { clearApiCache } from '@/features/doctor/serviceWorker';
import { ru } from '@/i18n/ru';

import { AuthContext, RoleRequiredError, type AuthState, type Role } from './authContext';

/** 409 role_required carries the user's roles. */
function requiredRoles(error: unknown): Role[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const detail = error.detail as { code?: unknown; roles?: unknown } | null;
  if (detail?.code !== 'role_required' || !Array.isArray(detail.roles)) return null;
  return detail.roles as Role[];
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const { message } = App.useApp();
  const [state, setState] = useState<AuthState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  // Restore the session from the stored refresh token (page reload, new tab).
  useEffect(() => {
    let cancelled = false;
    async function restore(): Promise<AuthState> {
      if (!tokenStore.getRefresh()) return { status: 'anonymous' };
      try {
        if (!tokenStore.getAccess() && !(await refreshSession())) return { status: 'anonymous' };
        return { status: 'authenticated', user: await api.get('/me') };
      } catch (error) {
        if (error instanceof NetworkError) return { status: 'offline' };
        tokenStore.clear();
        return { status: 'anonymous' };
      }
    }
    void restore().then((next) => {
      if (!cancelled) setState(next);
    });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  // The client cleared the tokens because the refresh token was rejected.
  useEffect(
    () =>
      tokenStore.onSessionLost(() => {
        queryClient.clear();
        setState({ status: 'anonymous' });
        void message.warning(ru.session.expired);
      }),
    [queryClient, message],
  );

  // Logout in another tab removes the refresh token from localStorage.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === tokenStore.REFRESH_KEY && event.newValue === null) {
        tokenStore.dropAccess();
        queryClient.clear();
        setState({ status: 'anonymous' });
      } else if (event.key === tokenStore.REFRESH_KEY) {
        // Rotated elsewhere: the next request refreshes with the new token.
        tokenStore.dropAccess();
      }
    }
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [queryClient]);

  const login = useCallback(async (loginName: string, password: string, role?: Role) => {
    let pair;
    try {
      pair = await api.post('/auth/login', {
        body: {
          login: loginName,
          password,
          device_id: deviceId(),
          device_label: deviceLabel(),
          ...(role ? { role } : {}),
        },
      });
    } catch (error) {
      const roles = requiredRoles(error);
      if (roles) throw new RoleRequiredError(roles);
      throw error;
    }
    tokenStore.set(pair);
    const user = await api.get('/me');
    setState({ status: 'authenticated', user });
    return user;
  }, []);

  const switchRole = useCallback(
    async (role: Role) => {
      const refreshToken = tokenStore.getRefresh();
      if (!refreshToken) throw new Error('no session');
      const pair = await api.post('/auth/switch-role', {
        body: { role, refresh_token: refreshToken },
      });
      tokenStore.set(pair);
      // Everything cached was read in the other role.
      queryClient.clear();
      await clearApiCache();
      const user = await api.get('/me');
      setState({ status: 'authenticated', user });
      return user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefresh();
    // The tablet stops being told about this doctor's calls, and the next person on it
    // must not read this one's patients offline.
    void unsubscribeFromPush();
    tokenStore.clear();
    queryClient.clear();
    void clearApiCache();
    setState({ status: 'anonymous' });
    if (refreshToken) {
      try {
        await api.post('/auth/logout', { body: { refresh_token: refreshToken } });
      } catch {
        // The token is already gone locally; the server copy expires on its own.
      }
    }
  }, [queryClient]);

  const retryRestore = useCallback(() => {
    setState({ status: 'loading' });
    setAttempt((n) => n + 1);
  }, []);

  const value = useMemo(
    () => ({ state, login, switchRole, logout, retryRestore }),
    [state, login, switchRole, logout, retryRestore],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
